/**
 * Consent Slice
 * Manages DPDP-compliant consent state for 8 categories
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ConsentCategory, ConsentEntry } from '@/types/onboarding';
import { CONSENT_CATEGORIES } from '@/types/onboarding';

export interface ConsentState {
  consents: ConsentEntry[];
  allRequiredGranted: boolean;
}

const initialState: ConsentState = {
  consents: CONSENT_CATEGORIES.map((c) => ({ ...c })),
  allRequiredGranted: false,
};

const computeAllRequired = (consents: ConsentEntry[]): boolean =>
  consents.filter((c) => c.required).every((c) => c.granted);

const consentSlice = createSlice({
  name: 'consent',
  initialState,
  reducers: {
    grantConsent: (state, action: PayloadAction<ConsentCategory>) => {
      const idx = state.consents.findIndex((c) => c.category === action.payload);
      if (idx !== -1) {
        state.consents[idx].granted = true;
        state.consents[idx].grantedAt = new Date().toISOString();
      }
      state.allRequiredGranted = computeAllRequired(state.consents);
    },

    revokeConsent: (state, action: PayloadAction<ConsentCategory>) => {
      const idx = state.consents.findIndex((c) => c.category === action.payload);
      if (idx !== -1 && !state.consents[idx].required) {
        // Cannot revoke required consents during onboarding
        state.consents[idx].granted = false;
        state.consents[idx].grantedAt = null;
      }
      state.allRequiredGranted = computeAllRequired(state.consents);
    },

    grantAllRequired: (state) => {
      state.consents.forEach((c) => {
        if (c.required) {
          c.granted = true;
          c.grantedAt = new Date().toISOString();
        }
      });
      state.allRequiredGranted = true;
    },

    grantAll: (state) => {
      state.consents.forEach((c) => {
        c.granted = true;
        c.grantedAt = new Date().toISOString();
      });
      state.allRequiredGranted = true;
    },

    resetConsents: () => initialState,

    // ── Hydrate from Firestore on login/session restore ──
    hydrateFromFirestore: (state, action: PayloadAction<string[]>) => {
      const grantedSet = new Set(action.payload);
      state.consents.forEach((c) => {
        if (grantedSet.has(c.category)) {
          c.granted = true;
          if (!c.grantedAt) c.grantedAt = new Date().toISOString();
        }
      });
      state.allRequiredGranted = computeAllRequired(state.consents);
    },
  },
});

export const {
  grantConsent,
  revokeConsent,
  grantAllRequired,
  grantAll,
  resetConsents,
  hydrateFromFirestore,
} = consentSlice.actions;

export default consentSlice.reducer;
